import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Check, Minus, Plus, ChevronLeft, Store, ClipboardCheck, Info } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { api } from '../lib/api';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [product, setProduct] = useState(null);
  const [mainImg, setMainImg] = useState(null);
  const [franchise, setFranchise] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [admState, setAdmState] = useState('abiertas');

  useEffect(() => {
    api.getScmeSettingsCached().then((s) => setAdmState(s.admissionsState || 'abiertas')).catch(() => {});
  }, []);

  useEffect(() => {
    setProduct(null); setNotFound(false); setMainImg(null);
    api.getProductBySlug(slug)
      .then(async (p) => {
        setProduct(p);
        if (p.seo?.metaTitle) document.title = p.seo.metaTitle;
        const franchises = await api.getFranchises();
        setFranchise(franchises.find((f) => f.id === p.ownerId) || null);
      })
      .catch(() => setNotFound(true));
    return () => { document.title = 'Natur Autoescuela Store'; };
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="text-stone-500 mb-4">Producto no encontrado.</p>
        <Link to="/tienda" className="text-teal-600 font-bold">Volver a la tienda</Link>
      </div>
    );
  }

  if (!product) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-stone-400">Cargando…</div>;

  const onAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} añadido al carrito`, {
      action: { label: 'Ver carrito', onClick: () => navigate('/carrito') },
    });
  };

  const onBuyNow = () => {
    addItem(product, qty);
    navigate('/checkout');
  };

  const galleryImages = [product.image, ...(product.gallery || [])].filter((v, i, a) => v && a.indexOf(v) === i);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <button onClick={() => navigate('/tienda')} className="flex items-center gap-1 text-sm text-stone-500 hover:text-stone-800 transition-colors mb-8" data-testid="back-to-tienda">
        <ChevronLeft className="w-4 h-4" /> Volver a la tienda
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="rounded-lg overflow-hidden shadow-md h-96 lg:h-[480px] bg-white">
            <img src={mainImg || product.image} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
          </div>
          {galleryImages.length > 1 && (
            <div className="flex gap-3 mt-4 flex-wrap" data-testid="product-gallery">
              {galleryImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setMainImg(src)}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${(mainImg || product.image) === src ? 'border-amber-400' : 'border-stone-200 hover:border-stone-400'}`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={src} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <h1 className="text-3xl md:text-4xl font-black text-stone-800 mb-2" data-testid="product-title">{product.name}</h1>
          {franchise && (
            <p className="flex items-center gap-1.5 text-sm text-teal-600 font-medium mb-4" data-testid="product-franchise">
              <Store className="w-4 h-4" /> Vendido por {franchise.name}
            </p>
          )}
          <div className="flex items-baseline gap-3 mb-6">
            <span className="text-4xl font-black text-stone-800" data-testid="product-price">{product.price.toFixed(2).replace('.', ',')} €</span>
            {product.comparePrice && <span className="text-xl text-stone-400 line-through">{product.comparePrice.toFixed(2).replace('.', ',')} €</span>}
            {product.comparePrice && <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-1 rounded">-{Math.round((1 - product.price / product.comparePrice) * 100)}%</span>}
          </div>

          <p className="text-stone-600 leading-relaxed mb-6">{product.description}</p>

          <ul className="space-y-2 mb-8">
            {(product.features || []).map((f, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-stone-700">
                <Check className="w-4 h-4 text-teal-500 shrink-0" /> {f}
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-4 mb-4">
            {product.saleMode !== 'libre' ? (
              <div className="w-full">
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 flex gap-3 mb-4" data-testid="admission-notice">
                  <Info className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-stone-600 leading-relaxed">
                    Este servicio está sujeto a <strong>disponibilidad real y admisión</strong>: revisamos capacidad,
                    profesorado, vehículos y zona antes de confirmar cada plaza para garantizar una atención adecuada.
                  </p>
                </div>
                <button onClick={() => navigate(`/admision?curso=${product.slug}`)} className="w-full bg-stone-800 hover:bg-stone-700 transition-colors text-white font-bold py-3.5 rounded text-sm flex items-center justify-center gap-2" data-testid="request-admission-detail">
                  <ClipboardCheck className="w-4 h-4" /> {admState === 'abiertas' ? 'Solicitar admisión' : 'Unirme a la lista de espera'}
                </button>
              </div>
            ) : (
              <>
            <div className="flex items-center border border-stone-300 rounded">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5 hover:bg-stone-100 transition-colors" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="w-10 text-center font-bold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-2.5 hover:bg-stone-100 transition-colors" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={onAdd} className="btn-natur flex items-center gap-2 px-6 py-3 rounded font-bold text-sm flex-1 justify-center" data-testid="add-to-cart-detail">
              <ShoppingCart className="w-4 h-4" /> Añadir al carrito
            </button>
              </>
            )}
          </div>
          {product.saleMode === 'libre' && (
          <button onClick={onBuyNow} className="w-full bg-stone-800 hover:bg-stone-700 transition-colors text-white font-bold py-3 rounded text-sm" data-testid="buy-now">
            Comprar ahora
          </button>
          )}

          {(product.faq || []).length > 0 && (
            <div className="mt-10">
              <h3 className="font-bold text-stone-800 mb-2">Preguntas frecuentes</h3>
              <Accordion type="single" collapsible>
                {product.faq.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-sm text-left">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm text-stone-500">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </div>

      {product.detailHtml && (
        <div className="product-rich mt-16 max-w-4xl" data-testid="product-detail-html" dangerouslySetInnerHTML={{ __html: product.detailHtml }} />
      )}
    </div>
  );
}
